import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { stringify } from "qs";
import { Card } from "main/javascripts/components/card/Card";
import { CardImage } from "main/javascripts/components/card/CardImage";
import { Cruise } from "main/javascripts/types/cruise";
import { CruiseIndexParam } from "main/javascripts/types/cruiseIndexParam";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { CruiseScheduleList } from "./CruiseScheduleList";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { CruiseRouteListModalDialog } from "./CruiseRouteListModalDialog";
import { GoogleTagManagerEventTypes } from "main/javascripts/constants/GoogleTagManagerEventTypes";

export interface IProps {
  cruise: Cruise.Cruise;
  param: CruiseIndexParam;
  isEventTracking?: boolean;
}

export const CruiseCard: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cruise, isEventTracking = false } = props;

  const { t } = useTranslation(["component", "label", "unit", "button"]);

  const vessel = cruise.vessel;
  const cruiseRoutes = cruise.cruiseRoutes || [];
  const schedules = cruise.cruiseSchedules || [];

  const [isCruiseRoutesModalDisplayed, setIsCruiseRoutesModalDisplayed] =
    useState(false);

  const hideCruiseRoutesModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCruiseRoutesModalDisplayed(false);
  };

  const displayCruiseRoutesModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCruiseRoutesModalDisplayed(true);
  };

  const showPagePath = useMemo(() => {
    if (schedules?.length > 0) {
      const params = { departure_date: schedules[0].departureDate };
      const paramsString = stringify(params);
      return `/cruises/${cruise.uniqueId}?${paramsString}`;
    }
    return `/cruises/${cruise.uniqueId}`;
  }, [schedules]);

  const imageUrl: string = vessel.vesselImages?.[0]?.imageUrl;

  return (
    <Card overflowStyle="visible">
      <div css={innerStyle}>
        <Link href={showPagePath} passHref>
          <a
            css={linkStyle}
            {...(isEventTracking
              ? {
                  "data-event-category":
                    GoogleTagManagerEventTypes.clickCruiseShow,
                }
              : {})}
          >
            <div css={basicInfoStyle}>
              <div css={imageBlockStyle}>
                <div css={imageStyle}>
                  <CardImage url={imageUrl} />
                </div>
              </div>
              <div css={infoBlockStyle}>
                <div css={titleStyle}>{cruise.title}</div>
                <div css={dataBlockStyle}>
                  <div>
                    {t("cruise.vesselName")}: {vessel.name}
                  </div>
                  <div>
                    {t("cruise.duration")}:{" "}
                    {t("unit:nights", { count: cruise.stays })}
                  </div>
                </div>
                <div css={dataBlockStyle}>
                  <div>
                    {t("cruise.departurePlace")}: {cruise.departurePortName}
                  </div>
                  {cruiseRoutes.length > 0 && (
                    <div>
                      {t("cruise.arrivalPlace")}:{" "}
                      {cruiseRoutes[cruiseRoutes.length - 1].title}
                    </div>
                  )}
                  <div css={showItineraryButtonBlockStyle}>
                    <div
                      css={showItineraryButtonStyle}
                      onClick={displayCruiseRoutesModal}
                    >
                      {t("button:cruise.showItinerary")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </Link>
        <div css={scheduleListStyle}>
          <CruiseScheduleList
            cruise={cruise}
            isEventTracking={isEventTracking}
          />{" "}
        </div>
      </div>
      <CruiseRouteListModalDialog
        displayed={isCruiseRoutesModalDisplayed}
        uniqueId={cruise.uniqueId}
        cruiseRoutes={cruise.cruiseRoutes}
        departureDate={schedules?.[0]?.departureDate}
        closeHandler={hideCruiseRoutesModal}
        isEventTracking={isEventTracking}
      />
    </Card>
  );
};

const innerStyle = css`
  width: 100%;
`;
const basicInfoStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  flex: 1;
  cursor: pointer;
`;
const imageBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: ${space.atom2x} ${space.atom2x} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    height: auto;
    width: 38%;
    padding: ${space.atom2_5x};
    padding-right: 0;
  }
`;
const imageStyle = css`
  aspect-ratio: 2 / 1;
  overflow: hidden;
  border-radius: ${borderRadius.normal};
  ${moreThanBreakpoint("tablet")} {
    aspect-ratio: 3 / 2;
  }
`;
const infoBlockStyle = css`
  width: 100%;
  box-sizing: border-box;
  padding: ${space.atom} ${space.atom};
  ${moreThanBreakpoint("tablet")} {
    width: 62%;
    padding: ${space.atom2x} ${space.atom1_5x};
  }
`;
const titleStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline1};
  font-weight: ${fontWeight.medium};
  color: ${textColor.primaryDarkColor};
  padding: ${space.atom} ${space.atom};
`;
const dataBlockStyle = css`
  padding: ${space.atom} ${space.atom};
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${textColor.secondaryDarkColor};
`;
const scheduleListStyle = css`
  width: 100%;
  border-top: 1px solid ${borderColor.primaryLightColor};
`;
const showItineraryButtonBlockStyle = css`
  display: flex;
  justify-content: flex-start;
  padding: ${space.atom1_5x} 0 0;
`;
const showItineraryButtonStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${accentColor.primaryColor};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.5rem;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
`;
const linkStyle = css`
  text-decoration: none;
`;
